const UCR_TIERS = [
  [2, "0–2", 46], [5, "3–5", 138], [20, "6–20", 276],
  [100, "21–100", 963], [1000, "101–1,000", 4592], [Infinity, "1,001+", 44836],
];

const STATE_NOTES = {
  CA: "California: get a CA Number and Motor Carrier Permit (MCP) from the DMV, and meet CARB clean-truck rules (Clean Truck Check).",
  NY: "New York: trucks over 18,000 lbs need a Highway Use Tax (HUT) certificate.",
  KY: "Kentucky: trucks over 59,999 lbs need a KYU weight-distance tax account.",
  NM: "New Mexico: trucks over 26,000 lbs need a weight-distance tax permit.",
  OR: "Oregon: trucks over 26,000 lbs pay weight-mile tax and need an Oregon account.",
  CT: "Connecticut: trucks 26,000 lbs and over pay the Highway Use Fee.",
  TX: "Texas: intrastate carriers need TxDMV operating authority and a Texas USDOT number.",
};

const has = (list, v) => (list || []).includes(v);
const money = (n) => `$${n.toLocaleString("en-US")}`;

function facts(p) {
  const details = (p.vehicles || []).map((v) => (p.vehicle_details || {})[v] || {});
  const cargo = p.cargo_types || [];
  const drivers = p.drivers || [];
  const ends = new Set(drivers.flatMap((d) => d.endorsements || []));
  const axleNum = (a) => (a === "6+" ? 6 : parseInt(a, 10) || 0);
  return {
    interstate: p.operation_type === "Interstate",
    intrastate: p.operation_type === "Intrastate",
    forHire: has(p.carrier_types, "For-Hire"),
    privateOnly: has(p.carrier_types, "Private") && !has(p.carrier_types, "For-Hire"),
    leasedOn: p.ownership === "lease_on",
    leaseOwn: p.ownership === "lease_own_authority",
    owned: p.ownership === "own",
    heavy: details.some((d) => ["26001_80000", "over_80000"].includes(d.gvwr)),
    threeAxle: details.some((d) => axleNum(d.axles) >= 3),
    over10k: details.some((d) => d.gvwr && d.gvwr !== "under_10001"),
    allLight: details.length > 0 && details.every((d) => d.gvwr === "under_10001"),
    hazmat: has(cargo, "Hazardous Materials (HAZMAT)"),
    tanker: has(cargo, "Tanker (Liquid/Gas)"),
    hhg: p.property_type === "Household Goods" || has(cargo, "Household Goods"),
    passengers: p.property_type === "Passengers",
    cargo,
    drivers,
    ends,
    cdl: drivers.some((d) => d.cdl_class),
    employees: drivers.some((d) => d.driver_type === "Employee Driver"),
    ownerOps: drivers.some((d) => d.driver_type === "Owner-Operator"),
  };
}

function ucrFee(n) {
  const tier = UCR_TIERS.find(([max]) => n <= max);
  return `Your fleet of ${n} falls in the ${tier[1]} bracket: ${money(tier[2])} per year (2026 fee schedule; confirm at plan.ucr.gov).`;
}

function insuranceMinimum(f) {
  if (f.passengers) return "Federal minimum for passenger carriers: $1,500,000 (15 or fewer seats) or $5,000,000 (16+ seats).";
  if (f.hazmat) return "Federal minimum with HAZMAT: $1,000,000 for oil and many hazardous materials, up to $5,000,000 for certain bulk or highly hazardous materials.";
  if (f.privateOnly) return "Private (non-HAZMAT) carriers have no federal insurance filing, but your state's minimum liability still applies.";
  if (f.allLight) return "Federal minimum for for-hire vehicles 10,000 lbs or less: $300,000 liability.";
  return "Federal minimum for for-hire general freight: $750,000 liability (most brokers and shippers ask for $1,000,000).";
}

export function filingGuidance(p) {
  const f = facts(p);
  const g = {};

  g.usdot = f.interstate
    ? { status: "required", notes: ["Required for interstate vehicles over 10,000 lbs, HAZMAT placards, or passengers."] }
    : f.intrastate
      ? { status: "check", notes: ["Many states require a USDOT number for intrastate carriers too. Check your state."] }
      : { status: "check", notes: [] };
  if (f.interstate && f.allLight && !f.hazmat && !f.passengers) g.usdot = { status: "check", notes: ["All your vehicles are 10,000 lbs or less, so a USDOT number may not be required federally."] };
  if (f.leasedOn) g.usdot.notes.push("Leased on to another carrier? You may operate under their USDOT number.");

  if (f.leasedOn) g.mc_authority = { status: "not_required", notes: ["You run under the carrier you're leased to, so you don't need your own MC authority."] };
  else if (f.privateOnly) g.mc_authority = { status: "not_required", notes: ["Private carriers hauling their own goods don't need MC authority."] };
  else if (f.intrastate) g.mc_authority = { status: "not_required", notes: ["MC authority is for interstate for-hire. Your state may require its own operating authority."] };
  else if (f.interstate && f.forHire) g.mc_authority = { status: "required", notes: [
    f.hhg ? "You need Household Goods authority, with extra consumer-protection rules." : f.passengers ? "You need passenger carrier authority." : "You need for-hire property authority.",
  ] };
  else g.mc_authority = { status: "check", notes: [] };

  g.boc3 = {
    status: g.mc_authority.status,
    notes: g.mc_authority.status === "not_required"
      ? ["Only needed if you hold your own MC authority."]
      : ["Names a process agent in every state where you operate, to accept legal papers for you.", "Must be filed within 90 days of receiving your MC#, or FMCSA can dismiss your application.", "Filed for you by a process agent company, not by you directly."],
  };

  g.bmc84 = { status: "not_required", notes: ["Only needed if you also act as a freight broker ($75,000 bond)."] };

  g.bmc91 = g.mc_authority.status === "required"
    ? { status: "required", notes: ["Your insurance company files this with FMCSA.", insuranceMinimum(f)] }
    : { status: "not_required", notes: ["Only needed when you hold your own MC authority."] };
  if (f.hhg && g.bmc91.status === "required") g.bmc91.notes.push("Household goods carriers also need a BMC-34 cargo insurance filing.");

  if (f.intrastate) g.ucr = { status: "not_required", notes: ["UCR applies only to interstate carriers."] };
  else if (f.leasedOn) g.ucr = { status: "not_required", notes: ["The carrier you're leased to usually covers your truck in their UCR."] };
  else if (f.interstate) g.ucr = { status: "required", notes: [p.fleet_size ? ucrFee(p.fleet_size) : "Add your fleet size in setup to see your UCR fee."] };
  else g.ucr = { status: "check", notes: [] };

  const qualified = f.heavy || f.threeAxle;
  const asked = p.ifta_required || p.irp_required;
  if (f.intrastate) g.irp_ifta = { status: "not_required", notes: ["IRP and IFTA are for travel across state lines. Use your state's regular registration and fuel tax."] };
  else if (f.interstate && (qualified || asked)) {
    g.irp_ifta = { status: "required", notes: [] };
    if (qualified) g.irp_ifta.notes.push("Your vehicles are over 26,000 lbs or have 3+ axles, so they qualify for IRP plates and IFTA decals.");
    else g.irp_ifta.notes.push("Your vehicle details don't show over 26,000 lbs or 3+ axles. Double-check before applying.");
    if (f.leasedOn) g.irp_ifta.notes.push("Your lease may include the carrier's IRP plates and IFTA. Check your lease.");
  } else g.irp_ifta = { status: "check", notes: ["Needed if your vehicles are over 26,000 lbs or have 3+ axles and cross state lines."] };

  const ins = [insuranceMinimum(f)];
  if (f.leasedOn) ins.push("Leased on: the carrier's policy covers you under dispatch. You need non-trucking (bobtail) liability and physical damage coverage.");
  if (f.leaseOwn) ins.push("Leased truck: the leasing company will require physical damage coverage and to be listed as loss payee.");
  if (f.owned) ins.push("You own the truck: physical damage coverage is optional but recommended.");
  if (f.employees) ins.push("Employee drivers: most states require workers' compensation insurance.");
  if (f.ownerOps) ins.push("Owner-operators: carriers often require occupational accident insurance.");
  if (f.cargo.some((c) => ["Refrigerated Food", "Fresh Produce"].includes(c))) ins.push("Reefer loads: add cargo coverage with reefer breakdown.");
  if (has(f.cargo, "Auto Transport")) ins.push("Auto transport: shippers often require $100,000–$250,000 cargo coverage.");
  if (has(f.cargo, "Livestock")) ins.push("Livestock: standard cargo policies exclude animals. Get livestock cargo coverage.");
  if (f.drivers.length) ins.push("List every driver on your policy. Insurers usually want 2+ years of CDL experience.");
  g.insurance = { status: f.privateOnly && !f.hazmat ? "check" : "required", notes: ins };

  if (p.eld_short_haul === true && p.eld_rods !== true) g.eld_enroll = { status: "not_required", notes: [
    "Short-haul exception: no logbook or ELD needed while you stay within 150 air-miles and return to the same location within 14 hours.",
    "Keep a time record for each driver (start time, end time, total hours) for 6 months.",
    "Any day you go beyond 150 air-miles or past 14 hours, you must keep a log for that day. More than 8 such days in 30 means you need an ELD.",
  ] };
  else if (p.eld_rods === true) g.eld_enroll = { status: "required", notes: ["Use a device from FMCSA's registered ELD list."] };
  else g.eld_enroll = { status: "check", notes: ["Exempt only if you're short-haul (150 air-miles), keep logs 8 days or fewer in 30, or drive a pre-2000 engine."] };
  if (p.eld_short_haul === true && p.eld_rods === true) g.eld_enroll.notes.push("You chose both RODS on an ELD and short-haul: short-haul days don't need a log, but keep the ELD for days outside 150 air-miles.");
  if (has(f.cargo, "Livestock")) g.eld_enroll.notes.push("Livestock haulers have an hours-of-service exemption within 150 air-miles of the loading point.");

  const safety = ["Every driver of a vehicle over 10,000 lbs needs a current DOT medical card."];
  if (f.interstate) safety.push("New interstate carriers get a New Entrant Safety Audit within 12 months. See the countdown below.");
  if (f.interstate && f.passengers) safety.push("Passenger carriers are audited sooner, within 120 days.");
  if (f.cargo.some((c) => ["Flatbed/Heavy Haul", "Building Materials", "Machinery/Equipment", "Oversized/Overweight Loads"].includes(c))) safety.push("Inspectors check cargo securement closely (49 CFR 393).");
  if (f.hazmat) safety.push("HAZMAT: carry shipping papers and emergency response info, and placard correctly.");
  g.safety_inspection = { status: "required", notes: safety };

  const drivers = p.drivers || [];
  const cdl = drivers.some((d) => d.cdl_class);
  g.drug_testing = {
    status: cdl || !drivers.length ? "required" : "check",
    notes: [
      "FMCSA requires a DOT drug & alcohol testing program (pre-employment, random, post-accident) for every CDL driver.",
      "Join a testing consortium, register on the Drug & Alcohol Clearinghouse, and run a query on each driver before they drive.",
    ],
  };
  if (!cdl && drivers.length) g.drug_testing.notes.unshift("None of your drivers lists a CDL class. You only need this if any driver needs a CDL.");

  return g;
}

export function overviewGuidance(p) {
  const f = facts(p);
  const sections = [];

  const permits = [];
  if (f.hazmat) permits.push("HAZMAT: drivers need the H (or X) endorsement with a TSA background check, PHMSA Hazmat Registration, and an FMCSA Hazmat Safety Permit for certain materials.");
  if (f.tanker) permits.push("Tanker: drivers need the N (or X) endorsement.");
  if (has(f.cargo, "Oversized/Overweight Loads") || has(f.cargo, "Flatbed/Heavy Haul")) permits.push("Oversize/overweight: get a permit from every state you travel through. Escort vehicles may be required.");
  if (f.hhg) permits.push("Household goods: give customers written estimates and the 'Ready to Move' booklet, and join a dispute arbitration program.");
  if (f.cargo.some((c) => ["Fresh Produce", "Refrigerated Food", "Beverages"].includes(c))) permits.push("Food loads: follow FDA Sanitary Transportation rules (temperature control and driver training).");
  if (has(f.cargo, "Livestock")) permits.push("Livestock: follow the federal 28-hour law for animal rest stops.");
  if (has(f.cargo, "Utilities")) permits.push("Utility service vehicles may qualify for an hours-of-service exemption.");
  if (f.passengers) permits.push("Passengers: drivers need the P endorsement.");
  if (permits.length) sections.push({ key: "permits", title: "Permits & endorsements for your cargo", notes: permits });

  const drv = [];
  if (f.cdl) {
    drv.push(f.ownerOps && !f.employees
      ? "Register in the FMCSA Drug & Alcohol Clearinghouse. As an owner-operator, you must also name a consortium (C/TPA) to run your testing."
      : "Register your company in the FMCSA Drug & Alcohol Clearinghouse. Run a full query before hiring each CDL driver, and a query on every driver yearly.");
    drv.push("Join a DOT drug & alcohol testing program. Every CDL driver needs a negative pre-employment test.");
  }
  if (f.employees) drv.push("Keep a driver qualification file for each employee driver (application, MVR, road test, medical card).");
  const warn = [];
  if (f.hazmat && !f.ends.has("H") && !f.ends.has("X")) warn.push("You selected HAZMAT cargo, but no driver has the H or X endorsement.");
  if (f.tanker && !f.ends.has("N") && !f.ends.has("X")) warn.push("You selected tanker cargo, but no driver has the N or X endorsement.");
  if (f.passengers && !f.ends.has("P")) warn.push("You transport passengers, but no driver has the P endorsement.");
  if (has(p.vehicles, "Semi-Tractor") && f.drivers.length && !f.drivers.some((d) => d.cdl_class === "A")) warn.push("Semi-tractor combinations usually need a Class A CDL, and no driver has Class A.");
  else if (f.heavy && f.drivers.length && f.drivers.every((d) => d.cdl_class === "C")) warn.push("Vehicles over 26,000 lbs need a Class A or B CDL, but your drivers only have Class C.");
  if (f.ends.has("T")) drv.push("T endorsement: some states limit where doubles and triples can run.");
  if (f.ends.has("S")) drv.push("S endorsement: school bus work needs extra state certification and background checks.");
  if (drv.length || warn.length) sections.push({ key: "drivers", title: "Drivers & Clearinghouse", notes: drv, warnings: warn });

  const states = [...new Set([p.home_state, p.operation_state].filter(Boolean))];
  const st = states.map((s) => STATE_NOTES[s]).filter(Boolean);
  if (f.intrastate) st.push("Intrastate: many states require their own USDOT number, operating authority, and insurance filing.");
  if (st.length) sections.push({ key: "states", title: "State-specific rules", notes: st });

  return sections;
}

export const STATUS_META = {
  required: { label: "Required for you", cls: "bg-[#FFD600] text-[#0A0A0A]" },
  check: { label: "Check if needed", cls: "border border-[#262626] text-white" },
  not_required: { label: "Likely not needed", cls: "border border-[#262626] text-muted-foreground" },
};
